/**
 * Every string the portal renders. One flat catalog: a new heading is one entry here plus one
 * `useText('…')` in the screen. Merchants override individual keys; unknown keys are dropped.
 *
 * Placeholders are `{name}` and are filled by formatText().
 */
export const DEFAULT_CONTENT = {
    'common.next': 'Next',
    'common.back': 'Back',
    'common.continue': 'Continue',
    'common.startOver': 'Start over',
    'common.submit': 'Submit claim',
    'panel.title': 'Let’s make this right',
    'panel.body': 'If your order was lost, damaged or stolen on its way to you, we’ll sort it out.',
    'step.order': 'Your order',
    'step.problem': 'What went wrong',
    'step.resolution': 'How we’ll fix it',
    'find.title': 'Let’s find your order',
    'find.emailLabel': 'Email address',
    'find.emailPlaceholder': 'you@example.com',
    'find.orderLabel': 'Order number',
    'find.orderPlaceholder': '#1001',
    'find.orderHint': 'You’ll find it in your order confirmation email.',
    'find.submit': 'Continue',
    'find.existingHint': 'Already filed a claim? Use the same details to check on it.',
    'otp.title': 'Check your email',
    'otp.body': 'We sent a 6-digit code to {email}.',
    'otp.codeLabel': 'Verification code',
    'otp.resendIn': 'Resend code in {seconds}s',
    'otp.resend': 'Resend code',
    'otp.spamHint': 'No email? Look in your spam folder.',
    'otp.submit': 'Verify',
    'status.title': 'You’ve already claimed this order',
    'status.body': 'Here’s where your claim for order {order} stands.',
    'status.statusLabel': 'Status',
    'status.submittedLabel': 'Submitted',
    'status.issueLabel': 'Issue',
    'status.contact': 'Questions? Contact us at {email}',
    'status.ineligibleTitle': 'This order can’t be claimed',
    'items.title': 'Which items had a problem?',
    'items.bulkLabel': 'Apply one issue to every item',
    'items.bulkPlaceholder': 'Choose an issue…',
    'items.qtyLabel': 'How many were affected?',
    'items.noteLabel': 'Tell us what happened',
    'items.clear': 'Clear',
    'items.count': '{count} items selected',
    'items.countOne': '1 item selected',
    'items.empty': 'There are no items on this order that can be claimed.',
    'issue.title': 'Tell us what happened',
    'issue.claimingLabel': 'You’re claiming',
    'issue.noteLabel': 'Describe the problem',
    'issue.notePlaceholder': 'What happened, and what does the damage look like?',
    'issue.photosLabel': 'Add photos',
    'issue.dropzone': 'Choose files or drag them here',
    'issue.dropzoneHint': 'JPG, PNG, WEBP, HEIC or PDF · up to 8 MB each',
    'issue.dropzoneMore': 'Add another file',
    'issue.optional': '(optional)',
    'resolution.title': 'How should we fix this?',
    'resolution.unavailable': 'Not available for the items you selected.',
    'reorder.title': 'Your replacement is ready to send',
    'reorder.body': 'Just check the delivery address below is right.',
    'reorder.itemsLabel': 'Items',
    'reorder.shipToLabel': 'Ship to',
    'reorder.edit': 'Edit',
    'reorder.submit': 'Send my replacement',
    'reorder.manualTitle': 'Where should we send it?',
    'reorder.manualBody': 'Check the delivery address before you send the claim.',
    'refund.title': 'Here’s your refund',
    'refund.body': 'Money back to your original payment method.',
    'refund.subtotal': 'Items',
    'refund.tax': 'Tax',
    'refund.shipping': 'Shipping',
    'refund.total': 'Total refund',
    'refund.submit': 'Confirm refund',
    'storeCredit.title': 'Here’s your store credit',
    'storeCredit.body': 'Credit added to your account, ready to spend on your next order.',
    'storeCredit.subtotal': 'Items',
    'storeCredit.tax': 'Tax',
    'storeCredit.shipping': 'Shipping',
    'storeCredit.total': 'Total credit',
    'storeCredit.submit': 'Confirm store credit',
    'success.reorder.title': 'Your replacement is on its way',
    'success.reorder.body': 'We’ve created the replacement order. The confirmation email is on its way too.',
    'success.refund.title': 'Your refund is on its way',
    'success.refund.body': 'It’s back with your bank now — how long it takes to land is up to them.',
    'success.storeCredit.title': 'Your store credit is ready',
    'success.storeCredit.body': 'It’s on your account now. Sign in at checkout to spend it.',
    'success.review.title': 'Your claim is with our team',
    'success.review.body': 'We’ll review it and email you as soon as there’s an update.',
    'success.button': 'Go Home',
    'error.notProtected': 'This order doesn’t include shipping protection.',
    'error.refunded': 'This order has already been refunded.',
    'error.notFulfilled': 'This order hasn’t shipped yet.',
    'error.notDelivered': 'This order hasn’t been delivered yet.',
    'error.windowExpired': 'The claim window for this order has closed.',
    'error.orderNotFound': 'We couldn’t find an order with those details.',
    'error.generic': 'Something went wrong. Please try again.',
};
const CONTENT_KEYS = Object.keys(DEFAULT_CONTENT);
export function isContentKey(key) {
    return Object.prototype.hasOwnProperty.call(DEFAULT_CONTENT, key);
}
/** `{name}` → vars.name. An unknown placeholder is left as written so it is obvious in the UI. */
export function formatText(template, vars) {
    if (!vars)
        return template;
    return template.replace(/\{(\w+)\}/g, (match, name) => {
        const value = vars[name];
        return value === undefined ? match : String(value);
    });
}
/**
 * Defaults, then the shop's overrides, then the locale's — later layers win, and only keys we know about
 * are taken, so a renamed or junk key falls back to ours instead of rendering blank.
 */
export function mergeContent(...layers) {
    const merged = { ...DEFAULT_CONTENT };
    for (const layer of layers) {
        if (!layer)
            continue;
        for (const key of CONTENT_KEYS) {
            const value = layer[key];
            if (typeof value === 'string' && value.trim())
                merged[key] = value;
        }
    }
    return merged;
}
//# sourceMappingURL=content.js.map